import React, { useState } from 'react';
import {
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CreditCardIcon,
  BoltIcon,
  ShieldCheckIcon,
  ClockIcon,
  BanknotesIcon,
  DocumentCheckIcon,
  ExclamationTriangleIcon,
  LightBulbIcon,
  ArrowRightIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';

const PayPalSetupGuide: React.FC = () => {
  const [expandedStep, setExpandedStep] = useState<number | null>(1);

  const toggleStep = (step: number) => {
    setExpandedStep(expandedStep === step ? null : step);
  };

  const cardClass = 'bg-white dark:bg-[#1c2432] border border-gray-200 dark:border-slate-700';
  const subTileClass = 'bg-gray-50 dark:bg-slate-900/60 border border-gray-200 dark:border-slate-700';

  const steps = [
    {
      id: 1,
      title: 'Bei PayPal einloggen',
      icon: CreditCardIcon,
      content: (
        <div className="space-y-4">
          <div className="rounded-2xl p-4" style={{ background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.30)' }}>
            <div className="flex items-start gap-3">
              <LightBulbIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-blue-800 dark:text-blue-200 mb-1">Tipp für den Start</p>
                <p className="text-sm text-blue-800/90 dark:text-blue-200/90">
                  Stellen Sie sicher, dass Sie sich mit dem PayPal-Konto anmelden, das Sie für den Vereinsempfang von Zahlungen verwenden.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            {[
              <>Öffnen Sie <a href="https://www.paypal.com" target="_blank" rel="noopener noreferrer" className="text-amber-700 dark:text-amber-300 hover:underline">www.paypal.com</a></>,
              <>Klicken Sie oben rechts auf "Einloggen"</>,
              <>Geben Sie Ihre E-Mail-Adresse und Ihr Passwort ein</>,
              <>Sie befinden sich jetzt im PayPal-Dashboard</>,
            ].map((content, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-500 text-slate-950 flex items-center justify-center text-xs font-black">{i + 1}</div>
                <p className="text-sm text-gray-800 dark:text-slate-200 font-medium pt-1">{content}</p>
              </div>
            ))}
          </div>
        </div>
      )
    },
    {
      id: 2,
      title: 'Verkäufer-Tools öffnen',
      icon: Cog6ToothIcon,
      content: (
        <div className="space-y-4">
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-500 text-slate-950 flex items-center justify-center text-xs font-black">1</div>
              <div>
                <p className="text-sm text-gray-800 dark:text-slate-200 font-medium mb-2">
                  Klicken Sie oben rechts auf das <strong>Zahnrad-Symbol (⚙)</strong> für "Einstellungen"
                </p>
                <div className={`rounded-xl p-3 ${subTileClass}`}>
                  <p className="text-xs text-gray-600 dark:text-slate-400">Sie finden es neben "Benachrichtigungen" und "Hilfe"</p>
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-500 text-slate-950 flex items-center justify-center text-xs font-black">2</div>
              <div>
                <p className="text-sm text-gray-800 dark:text-slate-200 font-medium mb-2">
                  Im linken Menü klicken Sie auf <strong>"Verkäufer-Tools"</strong>
                </p>
                <div className="rounded-xl p-3" style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}>
                  <p className="text-xs text-amber-800 dark:text-amber-200">
                    Menü-Reihenfolge: Konto → Sicherheit → Daten & Datenschutz → Zahlungen → Benachrichtigungen → <strong>Verkäufer-Tools</strong>
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-500 text-slate-950 flex items-center justify-center text-xs font-black">3</div>
              <div>
                <p className="text-sm text-gray-800 dark:text-slate-200 font-medium mb-2">
                  Sie sehen nun verschiedene Optionen. Klicken Sie auf <strong>"Website-Einstellungen"</strong>
                </p>
                <div className={`rounded-xl p-3 ${subTileClass}`}>
                  <p className="text-xs text-gray-600 dark:text-slate-400">
                    Unter "Online verkaufen" → "Website-Einstellungen": <br />
                    "Steuern Sie Ihre Online-Verkäufe, indem Sie Express-Checkouts aktivieren und Kunden nach der Bezahlung mit PayPal auf Ihre Website zurückleiten."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 3,
      title: 'Automatische Rückleitung aktivieren',
      icon: BoltIcon,
      content: (
        <div className="space-y-4">
          <div className="rounded-2xl p-4" style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}>
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-emerald-800 dark:text-emerald-200 mb-1">Wichtigster Schritt!</p>
                <p className="text-sm text-emerald-800/90 dark:text-emerald-200/90">
                  Hier aktivieren Sie, dass Mitglieder nach der Zahlung automatisch auf unsere Erfolgs-Seite zurückgeleitet werden.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-500 text-slate-950 flex items-center justify-center text-xs font-black">1</div>
              <div className="flex-1">
                <p className="text-sm text-gray-800 dark:text-slate-200 font-medium mb-2">
                  Scrollen Sie zu <strong>"Automatische Rückleitung für Websitezahlungen"</strong>
                </p>
                <div className={`rounded-xl p-3 ${subTileClass}`}>
                  <p className="text-xs text-gray-600 dark:text-slate-400 mb-2">
                    Aktivieren Sie die Option <strong>"Ein"</strong>
                  </p>
                  <div className="mt-2 p-2 rounded-lg bg-gray-100 dark:bg-slate-950/55 border border-gray-200 dark:border-slate-700">
                    <p className="text-xs font-mono text-amber-700 dark:text-amber-300">
                      https://intern.hsv-adlershof.berlin/paypal-success
                    </p>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
                    Tragen Sie diese URL in das Feld "Rückleitungs-URL" ein
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 4,
      title: 'Übertragung der Zahlungsdaten (PDT) aktivieren',
      icon: DocumentCheckIcon,
      content: (
        <div className="space-y-4">
          <div className="rounded-2xl p-4" style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}>
            <div className="flex items-start gap-3">
              <ExclamationTriangleIcon className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-amber-800 dark:text-amber-200 mb-1">Achtung: Notwendig für automatische Verbuchung!</p>
                <p className="text-sm text-amber-800/90 dark:text-amber-200/90">
                  PDT (Payment Data Transfer) ermöglicht es unserem System, die Zahlungsdetails von PayPal zu empfangen und automatisch die Verzehrkosten als bezahlt zu markieren.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-500 text-slate-950 flex items-center justify-center text-xs font-black">1</div>
              <div className="flex-1">
                <p className="text-sm text-gray-800 dark:text-slate-200 font-medium mb-2">
                  Scrollen Sie weiter nach unten zu <strong>"Übertragung der Zahlungsdaten (optional)"</strong>
                </p>
                <div className={`rounded-xl p-3 ${subTileClass}`}>
                  <p className="text-xs text-gray-600 dark:text-slate-400">Aktivieren Sie die Option <strong>"Ein"</strong></p>
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-500 text-slate-950 flex items-center justify-center text-xs font-black">2</div>
              <div className="flex-1">
                <p className="text-sm text-gray-800 dark:text-slate-200 font-medium mb-2">
                  Kopieren Sie das <strong>"Identitäts-Token"</strong>
                </p>
                <div className="rounded-xl p-3" style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}>
                  <p className="text-xs text-amber-800 dark:text-amber-200 mb-2">
                    <strong>Wichtig:</strong> Bewahren Sie dieses Token sicher auf! Es wird benötigt, wenn Sie PDT in Zukunft verifizieren müssen.
                  </p>
                  <div className="mt-2 p-2 rounded-lg bg-gray-100 dark:bg-slate-950/55 border border-gray-200 dark:border-slate-700">
                    <p className="text-xs font-mono text-gray-700 dark:text-slate-300 break-all">
                      Beispiel: 6J1w34UfPMjJQayKJM7PgdNjZSUrdcrkq7fb2aJhjru0sQjJpgFZ1KR3hoW
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-500 text-slate-950 flex items-center justify-center text-xs font-black">3</div>
              <p className="text-sm text-gray-800 dark:text-slate-200 font-medium pt-1">
                Klicken Sie unten auf <strong>"Speichern"</strong>
              </p>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 5,
      title: 'Fertig! Einrichtung testen',
      icon: CheckCircleIcon,
      content: (
        <div className="space-y-4">
          <div className="rounded-2xl p-4" style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}>
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="h-5 w-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-bold text-emerald-800 dark:text-emerald-200 mb-1">Glückwunsch!</p>
                <p className="text-sm text-emerald-800/90 dark:text-emerald-200/90">
                  Die PayPal-Integration ist nun vollständig eingerichtet. Sie können jetzt Testzahlungen durchführen.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-500 text-slate-950 flex items-center justify-center text-xs font-black">1</div>
              <p className="text-sm text-gray-800 dark:text-slate-200 font-medium pt-1">Gehen Sie zu "Meine Verzehrkosten"</p>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-500 text-slate-950 flex items-center justify-center text-xs font-black">2</div>
              <div>
                <p className="text-sm text-gray-800 dark:text-slate-200 font-medium mb-2">
                  Klicken Sie bei einem unbezahlten Eintrag auf "Mit PayPal bezahlen (Sandbox-Test)"
                </p>
                <div className="rounded-xl p-3" style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}>
                  <p className="text-xs text-amber-800 dark:text-amber-200">
                    <strong>Sandbox-Test:</strong> Nutzen Sie zunächst den Sandbox-Button für Testzahlungen ohne echtes Geld. Erst wenn alles funktioniert, nutzen Sie den echten PayPal-Button.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-500 text-slate-950 flex items-center justify-center text-xs font-black">3</div>
              <div>
                <p className="text-sm text-gray-800 dark:text-slate-200 font-medium mb-2">
                  Nach erfolgreicher Zahlung werden Sie auf die Erfolgs-Seite weitergeleitet
                </p>
                <div className={`rounded-xl p-3 ${subTileClass}`}>
                  <p className="text-xs text-gray-600 dark:text-slate-400">
                    Dort sehen Sie alle Zahlungsdetails und die bezahlten Verzehrkosten werden automatisch im System als "bezahlt" markiert.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen font-sans text-gray-900 dark:text-slate-200" style={{ background: 'transparent', WebkitFontSmoothing: 'antialiased' }}>
      <div className="max-w-7xl mx-auto py-2 sm:py-4">
        {/* Hero */}
        <section className="pt-2 pb-6 px-4 sm:px-6">
          <div className="max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.22em] text-amber-600/80 dark:text-amber-400/80 font-semibold mb-3">
              ADMIN · PayPal-Setup
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-gray-900 dark:text-white leading-[1.05] tracking-tight" style={{ letterSpacing: '-0.04em' }}>
              PayPal-<br /><span className="text-amber-600 dark:text-amber-400">Setup.</span>
            </h1>
            <p className="text-gray-700 dark:text-slate-300 text-base md:text-lg leading-relaxed max-w-2xl mt-4">
              Schritt-für-Schritt-Anleitung zur Aktivierung der automatischen PayPal-Verbuchung —
              von der Anmeldung bis zum PDT-Token, damit Verzehrkosten <span className="font-bold text-emerald-700 dark:text-emerald-300">ohne Handarbeit</span> als bezahlt markiert werden.
            </p>
          </div>
        </section>

        <div className="px-4 sm:px-6 space-y-6">
          {/* Why is this important */}
          <div className="rounded-3xl p-6" style={{ background: 'rgba(59,130,246,0.10)', border: '1px solid rgba(59,130,246,0.30)' }}>
            <div className="flex items-start gap-3 mb-4">
              <LightBulbIcon className="h-6 w-6 text-blue-600 dark:text-blue-400 flex-shrink-0" />
              <h2 className="text-lg font-black text-gray-900 dark:text-white tracking-tight">Warum ist diese Einrichtung notwendig?</h2>
            </div>
            <p className="text-sm text-blue-800/90 dark:text-blue-200/90 mb-4">
              Ohne die korrekte PayPal-Einrichtung müssen Zahlungen <strong>manuell</strong> im System verbucht werden. Das bedeutet:
            </p>
            <ul className="space-y-2 text-sm text-blue-800/90 dark:text-blue-200/90">
              {[
                'Der Kassenwart muss jede PayPal-Zahlung händisch mit den Verzehrkosten abgleichen',
                'Mitglieder wissen nicht sofort, ob ihre Zahlung angekommen ist',
                'Doppelzahlungen oder Fehler sind schwerer zu erkennen',
                'Viel zusätzlicher Verwaltungsaufwand',
              ].map((s, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-rose-700 dark:text-rose-300 font-black mt-0.5">×</span>
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Benefits */}
          <div className="rounded-3xl p-6" style={{ background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)' }}>
            <div className="flex items-center gap-3 mb-4">
              <CheckCircleIcon className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              <h2 className="text-lg font-black text-gray-900 dark:text-white tracking-tight">Vorteile der automatischen Integration</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                { Icon: BoltIcon, title: 'Automatische Verbuchung', desc: 'Zahlungen werden sofort als "bezahlt" im System markiert – ohne manuelle Arbeit' },
                { Icon: ClockIcon, title: 'Zeitersparnis', desc: 'Keine manuellen Abgleiche mehr notwendig – spart Stunden an Verwaltungsarbeit' },
                { Icon: ShieldCheckIcon, title: 'Sicher & zuverlässig', desc: 'Transaktions-IDs werden automatisch gespeichert für vollständige Nachvollziehbarkeit' },
                { Icon: BanknotesIcon, title: 'Besserer Überblick', desc: 'Mitglieder sehen sofort, für welche Events sie bezahlt haben' },
              ].map(({ Icon, title, desc }) => (
                <div key={title} className="flex items-start gap-3">
                  <Icon className="h-5 w-5 text-emerald-700 dark:text-emerald-300 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-bold text-emerald-900 dark:text-emerald-100">{title}</p>
                    <p className="text-xs text-emerald-800/80 dark:text-emerald-200/80">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Step-by-step guide */}
          <div className={`rounded-3xl p-6 ${cardClass}`}>
            <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight mb-4">Schritt-für-Schritt-Anleitung</h2>

            <div className="space-y-3">
              {steps.map((step) => {
                const Icon = step.icon;
                const isExpanded = expandedStep === step.id;

                return (
                  <div key={step.id} className="rounded-2xl overflow-hidden border border-gray-200 dark:border-slate-700">
                    <button
                      onClick={() => toggleStep(step.id)}
                      className={`w-full flex items-center justify-between p-4 transition-colors ${
                        isExpanded
                          ? 'bg-amber-100/60 dark:bg-amber-500/[0.06]'
                          : 'bg-gray-50 dark:bg-slate-900/40'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30">
                          <Icon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                        </div>
                        <div className="text-left">
                          <p className="text-sm font-bold text-gray-900 dark:text-white">Schritt {step.id}: {step.title}</p>
                        </div>
                      </div>
                      {isExpanded ? <ChevronUpIcon className="h-5 w-5 text-gray-600 dark:text-slate-400" /> : <ChevronDownIcon className="h-5 w-5 text-gray-600 dark:text-slate-400" />}
                    </button>
                    {isExpanded && <div className="p-4 bg-gray-50 dark:bg-slate-950/30">{step.content}</div>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Important Notes */}
          <div className="rounded-3xl p-6" style={{ background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)' }}>
            <div className="flex items-start gap-3 mb-4">
              <ExclamationTriangleIcon className="h-6 w-6 text-amber-600 dark:text-amber-400 flex-shrink-0" />
              <h2 className="text-lg font-black text-gray-900 dark:text-white tracking-tight">Wichtige Hinweise</h2>
            </div>
            <ul className="space-y-2 text-sm text-amber-800/90 dark:text-amber-200/90">
              <li className="flex items-start gap-2">
                <ArrowRightIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-700 dark:text-amber-300" />
                <span>
                  <strong>Rückleitungs-URL:</strong> Muss exakt <code className="bg-amber-500/15 border border-amber-500/30 px-1 rounded">https://intern.hsv-adlershof.berlin/paypal-success</code> lauten
                </span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRightIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-700 dark:text-amber-300" />
                <span><strong>PDT aktivieren:</strong> Ohne PDT kann das System die Zahlungsdetails nicht empfangen</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRightIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-700 dark:text-amber-300" />
                <span><strong>Identitäts-Token:</strong> Bewahren Sie dieses Token sicher auf (z.B. in einem Passwort-Manager)</span>
              </li>
              <li className="flex items-start gap-2">
                <ArrowRightIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-amber-700 dark:text-amber-300" />
                <span><strong>Sandbox testen:</strong> Testen Sie immer zuerst mit dem Sandbox-Account, bevor Sie echte Zahlungen akzeptieren</span>
              </li>
            </ul>
          </div>

          {/* Summary */}
          <div className={`rounded-3xl p-6 ${cardClass}`}>
            <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight mb-4">Zusammenfassung</h2>
            <p className="text-sm text-gray-600 dark:text-slate-400 mb-4">Nach Abschluss dieser Einrichtung passiert Folgendes bei jeder PayPal-Zahlung:</p>
            <div className="space-y-3">
              {[
                'Mitglied klickt auf "Mit PayPal bezahlen" bei seinen Verzehrkosten',
                'Zahlung wird bei PayPal durchgeführt',
                'PayPal leitet das Mitglied auf unsere Erfolgs-Seite zurück (mit allen Zahlungsdaten)',
              ].map((text, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-700 dark:text-amber-300 flex items-center justify-center text-xs font-black">{i + 1}</div>
                  <p className="text-sm text-gray-700 dark:text-slate-300 pt-1">{text}</p>
                </div>
              ))}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-700 dark:text-amber-300 flex items-center justify-center text-xs font-black">4</div>
                <p className="text-sm text-gray-700 dark:text-slate-300 pt-1">
                  Unser System markiert die Verzehrkosten <strong>automatisch</strong> als bezahlt
                </p>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-700 dark:text-emerald-300 flex items-center justify-center text-sm font-black">✓</div>
                <p className="text-sm text-emerald-700 dark:text-emerald-300 font-bold pt-1">Fertig! Keine manuelle Arbeit mehr notwendig.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayPalSetupGuide;
